import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Recommendation from "../../components/Recommendation/Recommendation";
import MusicLoader from "../../components/MusicLoader/MusicLoader";
import { getRecommendations } from "../../api/spotify";
import { saveSong, deleteSong, getSaved } from "../../api/saved";
import { apiError } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import "./Results.scss";

export default function Results() {
  const { artist, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [recommendations, setRecommendations] = useState(null);
  const [iframeApi, setIframeApi] = useState();
  const [status, setStatus] = useState("loading"); // loading | done | error | empty
  const [error, setError] = useState("");
  const [savedMap, setSavedMap] = useState({}); // artist_uri -> saved song id
  const [savingUri, setSavingUri] = useState(null);

  // Register the Spotify iframe API callback.
  useEffect(() => {
    window.onSpotifyIframeApiReady = (api) => setIframeApi(api);
  }, []);

  // Load which of these are already saved so the hearts reflect reality.
  useEffect(() => {
    if (!user) {
      setSavedMap({});
      return;
    }
    let active = true;
    getSaved()
      .then((list) => {
        if (!active) return;
        const map = {};
        list.forEach((s) => (map[s.artist_uri] = s.id));
        setSavedMap(map);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user]);

  // Fetch recommendations whenever the target artist changes.
  useEffect(() => {
    let active = true;
    setStatus("loading");
    setRecommendations(null);

    // Recommendations already include the AI "why" (generated with the picks),
    // so no second round-trip is needed.
    getRecommendations(artist, id)
      .then((data) => {
        if (!active) return;
        setRecommendations(data);
        setStatus(data.length ? "done" : "empty");
      })
      .catch((err) => {
        if (!active) return;
        setError(apiError(err, "Could not load recommendations"));
        setStatus("error");
      });

    return () => {
      active = false;
    };
  }, [artist, id]);

  const handleToggleSave = async (recommendation) => {
    if (!user) {
      toast("Log in to save your finds", "error");
      navigate("/login");
      return;
    }
    const uri = recommendation.artist_uri;
    const existingId = savedMap[uri];
    setSavingUri(uri);
    try {
      if (existingId) {
        // Unsave
        await deleteSong(existingId);
        setSavedMap((prev) => {
          const next = { ...prev };
          delete next[uri];
          return next;
        });
        toast("Removed from your finds");
      } else {
        // Save
        const song = await saveSong({
          artist_id: recommendation.artist_id,
          artist_name: recommendation.artist_name,
          track_title: recommendation.track_title,
          artist_image: recommendation.artist_image,
          popularity: recommendation.popularity,
          artist_uri: uri,
          chatGPT: recommendation.chatGPT || "",
        });
        setSavedMap((prev) => ({ ...prev, [uri]: song.id }));
        toast("Saved to your finds");
      }
    } catch (err) {
      toast(apiError(err, "Something went wrong"), "error");
    } finally {
      setSavingUri(null);
    }
  };

  return (
    <div className="results">
      <Header />
      <main className="results__main">
        <header className="results__intro rise-1">
          <p className="results__eyebrow">
            <span>02</span> <i /> Your gateway to classical
          </p>
          <h1 className="results__title">
            If you like <span className="results__artist">{artist}</span>
          </h1>
          <p className="results__subtitle">
            Here's classical music worth exploring — with a note on why each one fits.
          </p>
        </header>

        <section className="results__list">
          {status === "loading" && (
            <MusicLoader
              steps={[
                `Listening to ${artist}…`,
                "Consulting the composers…",
                "Cross-referencing four centuries…",
                "Finding your classical matches…",
              ]}
            />
          )}

          {status === "error" && (
            <p className="results__message">{error}</p>
          )}

          {status === "empty" && (
            <p className="results__message">
              We couldn't find classical matches for {artist}. Try another
              artist.
            </p>
          )}

          {status === "done" &&
            recommendations.map((rec, i) => (
              <Recommendation
                key={rec.artist_uri}
                index={i}
                recommendation={rec}
                onToggleSave={handleToggleSave}
                saved={Boolean(savedMap[rec.artist_uri])}
                saving={savingUri === rec.artist_uri}
                iframeApi={iframeApi}
              />
            ))}
        </section>
      </main>
      <Footer />
    </div>
  );
}
